import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { PurchaseEventCreation } from 'components/domain/taxation/PurchaseEventCreation'
import { PurchaseEventDetails } from 'components/domain/taxation/PurchaseEventDetails'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { useFetchPurchaseEvents } from 'lib/hooks/useFetchPurchaseEvents'
import { MAX_LISTING_TABS } from 'lib/hooks/useListingTabsWithStocks'
import { FetchedStockInterface, useStockStore } from 'lib/hooks/useStockStore'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InventoryArticle, PurchaseEvent, roundTo2Decimals } from 'shared'
import { SetState } from 'types'
import {
  LINKED_LOT_ICON,
  ListingTabsState,
  LISTING_AND_APPRAISAL,
  STOCK_DISPLAY,
} from 'utils/constants'
import { currencySymbol, getProperty } from '../../../../lib/utils'
import { DockAction } from '../InventoryPresenter'
import ChangeViewRow from './ChangeViewRow'
import HeaderRow from './HeaderRow'
import { ResponsiveGrid } from './ResponsiveGrid'
import './stock-display.css'
import { Sort } from './StockDisplayUtils'

interface StockDisplayProps {
  openPriceSuggestDrawer?: (article: InventoryArticle, view?: 'buyPrice' | 'sellPrice') => void
  openReservedQuantityDrawer: (article: InventoryArticle) => void
  selectArticle: (article: InventoryArticle) => void
  setStock: SetState<InventoryArticle[]>
  stockLength: number
  filteredStockLength: number
  revertArticleEdit: (article: InventoryArticle) => void
  showLocations: (article: InventoryArticle) => void
  sortRefresh: string
  articleWithChangedPriceExists: boolean
  emptyStockText: string
  viewOnly: boolean
  areFiltersTouched: boolean
  onDeleteArticle?: (article: InventoryArticle) => void
  totalFilteredStockValues: {
    price: number
    quantity: number
    newPrice: number
    newQuantity: number
    buyPrice: number
    uniqueArticles: number
  }
  filteredStock: InventoryArticle[]
  parentPage: string
  selectedCount: number
  selectedStockValue: number
  refreshStockAction?: DockAction
  editedStockExists: boolean

  listingTabsState?: ListingTabsState
  allListingStocks?: Record<number, FetchedStockInterface>
  onAddListingTab?: () => void
  onActivateListingTab?: (id: number) => void
  onCloseListingTab?: ((id: number) => void) | undefined
  onRenameListingTab?: (id: number, newTitle: string) => void
  onAssociateListingTabToLot?: (tabId: number, lot: PurchaseEvent) => void
  unlinkListingTabFromLot?: (tabId: number) => void
  moveAllStockToTab: ((targetTabId: number) => void) | undefined
}

const calcPriceDiff = ({ price, newPrice }: InventoryArticle): number => {
  if (!newPrice) return 0
  return Math.floor((newPrice / price - 1) * 10000) / 100
}

const stripChars = (str: string) => (typeof str === 'string' ? str.replace(/\D+/g, '') : str)

export const applySort = (stock: InventoryArticle[], sort: Sort): InventoryArticle[] => {
  const dir = sort.direction === 'asc' ? 1 : -1

  return [...stock].sort((a, b) => {
    let result = 0

    if (sort.type === 'string') {
      let firstItem = ''
      let secondItem = ''
      if (sort.by === 'locationName') {
        firstItem = a.locations![0].name || ''
        secondItem = b.locations![0].name || ''
      } else {
        firstItem = String(getProperty(sort.by, a) || '')
          .trim()
          .toLowerCase()
        secondItem = String(getProperty(sort.by, b) || '')
          .trim()
          .toLowerCase()
      }

      return firstItem.localeCompare(secondItem) * (sort.direction === 'asc' ? 1 : -1)
    } else if (sort.type === 'number') {
      let valA: any = getProperty(sort.by, a)
      let valB: any = getProperty(sort.by, b)

      valA = Number(stripChars(valA))
      valB = Number(stripChars(valB))

      if (sort.by === 'price') {
        valA = a.newPrice ?? valA
        valB = b.newPrice ?? valB
      }

      result = (valA - valB) * dir
    } else if (sort.type === '% diff') {
      result = (calcPriceDiff(a) - calcPriceDiff(b)) * dir
    } else if (sort.type === 'date') {
      const dateA = a.listedAt ? new Date(a.listedAt).getTime() : null
      const dateB = b.listedAt ? new Date(b.listedAt).getTime() : null

      if (dateA !== null && dateB !== null) {
        result = (dateA - dateB) * dir
      } else if (dateA === null && dateB === null) {
        result = a.card.name.localeCompare(b.card.name) * dir
      } else {
        result = dateA === null ? 1 : -1
      }
    }

    // tie-breaker su idArticle DESC
    if (result === 0) {
      result = Number(b.idArticle) - Number(a.idArticle)
    }

    return result
  })
}

export const StockDisplay = React.memo(function StockDisplay({
  selectArticle,
  openPriceSuggestDrawer,
  openReservedQuantityDrawer,
  revertArticleEdit,
  showLocations,
  stockLength,
  filteredStockLength,
  areFiltersTouched,
  onDeleteArticle,
  totalFilteredStockValues,
  filteredStock,
  emptyStockText,
  parentPage,
  selectedCount,
  selectedStockValue,
  editedStockExists,

  listingTabsState,
  allListingStocks,
  onAddListingTab,
  onActivateListingTab,
  onCloseListingTab,
  onRenameListingTab,
  onAssociateListingTabToLot,
  unlinkListingTabFromLot,
  moveAllStockToTab,
}: StockDisplayProps): JSX.Element {
  const { t } = useTranslation()
  const { user } = useUser()
  const activeStockType = useStockStore((state) => state.activeStockType)
  const stockLock = useStockStore((state) => state.stockLock)
  const [confirmCloseTabDialog, setConfirmCloseTabDialog] = useState(false)
  const [tabIdToClose, setTabIdToClose] = useState<number | null>(null)

  const stockIsEmpty = !stockLength
  const stockIsHiddenByFilters = !!(stockLength && !filteredStockLength)

  const [selectedSize, setSelectedSize] = useState(5)

  //  main menu on the tab (chevron)
  const [tabMenuAnchorEl, setTabMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [tabMenuTabId, setTabMenuTabId] = useState<number | null>(null)

  // lots submenu
  const [lotMenuAnchorEl, setLotMenuAnchorEl] = useState<HTMLElement | null>(null)

  const [editingTabId, setEditingTabId] = useState<number | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  const renameInputRef = React.useRef<HTMLInputElement | null>(null)

  const { purchaseEvents, loading: loadingLots } = useFetchPurchaseEvents()
  const [openCreateLotDialog, setOpenCreateLotDialog] = useState(false)
  const [openPurchaseEventDetail, setOpenPurchaseEventDetail] = useState(false)
  const [currentPurchaseEvent, setCurrentPurchaseEvent] = useState<PurchaseEvent | undefined>(
    undefined
  )

  const currentTab =
    tabMenuTabId && listingTabsState
      ? listingTabsState.tabs.find((t) => t.id === tabMenuTabId)
      : undefined

  const isTabZero = currentTab && listingTabsState?.tabs[0]?.id === currentTab.id

  const isAssociated = !!currentTab?.lotId
  const canAddTab = !!listingTabsState && listingTabsState.tabs.length < MAX_LISTING_TABS

  // Reads the choice from local storage at startup and updates the state
  useEffect(() => {
    const storedWidth = localStorage.getItem('selectedCardsSize')
    if (storedWidth !== null) {
      setSelectedSize(parseInt(storedWidth, 10))
    }
  }, [])

  // utilizzato per chiudere il menu all'interno del tour
  useEffect(() => {
    const handler = () => setTabMenuAnchorEl(null)
    window.addEventListener('pt:close-tab-menu', handler)
    return () => window.removeEventListener('pt:close-tab-menu', handler)
  }, [setTabMenuAnchorEl])

  useEffect(() => {
    if (editingTabId == null) return
    const handler = (e: MouseEvent) => {
      const wrapper = renameInputRef.current?.closest('.stock-tab-rename-input')
      if (wrapper && e.target instanceof Node && wrapper.contains(e.target)) return
      e.preventDefault()
      e.stopPropagation()
      const trimmed = editingTitle.trim()
      if (trimmed && onRenameListingTab) {
        onRenameListingTab(editingTabId, trimmed)
      }
      setEditingTabId(null)
      setEditingTitle('')
    }
    document.addEventListener('click', handler, true)
    return () => document.removeEventListener('click', handler, true)
  }, [editingTabId, editingTitle, onRenameListingTab])

  return (
    <>
      {stockLock && parentPage === STOCK_DISPLAY ? (
        <div className="w-100 d-flex flex-column align-items-center loading-progress">
          <h6 className="pb-5">{t('stockPricing.updatingStock')}</h6>
          <CircularProgress size={100} />
        </div>
      ) : (
        <div>
          <div className="stock-display-wrapper">
            {/* --- TAB BAR  --- */}
            {parentPage === LISTING_AND_APPRAISAL && listingTabsState && (
              <div className="stock-tabs-bar">
                {listingTabsState.tabs.map((tab, index) => {
                  const tabStock = allListingStocks?.[tab.id]
                  const singlesCount = tabStock?.stock?.length ?? 0
                  const sealedCount = tabStock?.sealedStock?.length ?? 0

                  return (
                    <div
                      key={tab.id}
                      data-testid={`tab-${tab.id}`}
                      className={
                        'stock-tab' +
                        (tab.id === listingTabsState.activeTabId ? ' stock-tab--active' : '')
                      }
                    >
                      {/* name container / input */}
                      <div
                        onClick={() =>
                          editingTabId == null &&
                          onActivateListingTab &&
                          onActivateListingTab(tab.id)
                        }
                      >
                        {editingTabId === tab.id ? (
                          <TextField
                            value={editingTitle}
                            inputRef={renameInputRef}
                            name="rename-tab"
                            autoFocus
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const trimmed = editingTitle.trim()
                                if (trimmed && onRenameListingTab) {
                                  onRenameListingTab(tab.id, trimmed)
                                }
                                setEditingTabId(null)
                                setEditingTitle('')
                              }
                              if (e.key === 'Escape') {
                                setEditingTabId(null)
                                setEditingTitle('')
                              }
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" style={{ width: '38px' }}>
                                  <i
                                    className="fa-light fa-check"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      const trimmed = editingTitle.trim()
                                      if (trimmed && onRenameListingTab) {
                                        onRenameListingTab(tab.id, trimmed)
                                      }
                                      setEditingTabId(null)
                                      setEditingTitle('')
                                    }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                            className="stock-tab-rename-input"
                          />
                        ) : (
                          <div className="d-flex flex-row h-100 align-items-center">
                            {tab.lotId !== undefined && (
                              <img
                                alt="linkedLot"
                                data-testid="tab-linked-to-event"
                                src={LINKED_LOT_ICON}
                                style={{ height: '25px', width: 'auto' }}
                              />
                            )}
                            <div className="d-flex flex-column">
                              <span data-testid={tab.title} className="stock-tab-label">
                                {tab.title}
                              </span>
                              <Typography variant="caption" data-testid="quantity-in-tab">
                                {' '}
                                {t('inventory.tabs.singles')} : {singlesCount} |{' '}
                                {t('inventory.tabs.sealed')} : {sealedCount}
                              </Typography>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* tab actions: menu + close */}
                      {!editingTabId && (
                        <div className="stock-tab-actions">
                          <div
                            className="listing-tab-btn"
                            data-testid={`action-tab-${tab.id}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              setTabMenuAnchorEl(e.currentTarget)
                              setTabMenuTabId(tab.id)
                            }}
                          >
                            <i className="fa-regular fa-chevron-down " />
                          </div>

                          {index !== 0 && (
                            <div
                              className="listing-tab-btn"
                              data-testid={`close-tab-${tab.id}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                const tabStock = allListingStocks?.[tab.id]
                                if (
                                  (tabStock?.stock?.length ?? 0) > 0 ||
                                  (tabStock?.sealedStock?.length ?? 0) > 0
                                ) {
                                  setTabIdToClose(tab.id)
                                  setConfirmCloseTabDialog(true)
                                } else {
                                  onCloseListingTab && onCloseListingTab(tab.id)
                                }
                              }}
                            >
                              <i className="fa-regular fa-xmark "></i>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}

                {canAddTab && (
                  <button
                    className="stock-tab-add"
                    data-testid="add-new-tab"
                    type="button"
                    onClick={() => {
                      if (!canAddTab || !onAddListingTab) {
                        return
                      }
                      onAddListingTab()
                    }}
                  >
                    <span className="stock-tab-add-icon">+</span>
                  </button>
                )}
              </div>
            )}

            <div className="stock-display-container">
              {/* --- MAIN CONTAINER--- */}
              {!stockIsEmpty && (
                <div className="d-flex justify-content-between flex-wrap pt-3">
                  <HeaderRow>
                    <div>
                      <span className="text-uppercase">
                        {/* {t('stockPricing.results', { count: filteredStockLength })} */}
                        {totalFilteredStockValues.uniqueArticles} UNIQUE ARTICLES
                      </span>

                      <span className="text-uppercase">
                        {areFiltersTouched && ` ${t('stockPricing.filtersActive')}`}
                      </span>
                    </div>

                    <div className="mx-2">
                      {totalFilteredStockValues.quantity}
                      {totalFilteredStockValues.quantity !==
                        totalFilteredStockValues.newQuantity && (
                        <>
                          <i className="fa-solid fa-arrow-right mx-1"></i>
                          {totalFilteredStockValues.newQuantity}
                        </>
                      )}{' '}
                      {activeStockType === 'singles'
                        ? t('autopriceArticles.summary.cards')
                        : t('autopriceArticles.summary.articles')}
                      <span>(stock - sold)</span>
                    </div>

                    <div className="mx-2">
                      {totalFilteredStockValues.price}
                      {currencySymbol(user?.currency)}
                      {totalFilteredStockValues.price !== totalFilteredStockValues.newPrice && (
                        <>
                          <i className="fa-solid fa-arrow-right mx-1"></i>
                          {totalFilteredStockValues.newPrice}
                          {currencySymbol(user?.currency)}
                        </>
                      )}{' '}
                      {activeStockType === 'singles'
                        ? t('stockPricing.cardValue')
                        : t('stockPricing.articleValue')}
                      <span>(stock - sold)</span>
                    </div>

                    {user.featureFlags?.taxation && totalFilteredStockValues.buyPrice > 0 && (
                      <div className="mx-2">
                        {totalFilteredStockValues.buyPrice}
                        {currencySymbol(user?.currency)} {t('stockPricing.buyValue')}
                      </div>
                    )}

                    <div className="mx-2">
                      {t('stockPricing.selected', { count: selectedCount })}
                      {selectedCount > 0 && (
                        <span style={{ marginLeft: '4px' }}>
                          ({selectedStockValue} {currencySymbol(user?.currency)})
                        </span>
                      )}
                    </div>
                  </HeaderRow>

                  <ChangeViewRow
                    parentPage={parentPage}
                    selectedSize={selectedSize}
                    setSelectedSize={setSelectedSize}
                    editedStockExists={editedStockExists}
                  />
                </div>
              )}
              {(stockIsEmpty || stockIsHiddenByFilters) && (
                <Box paddingTop={7} style={{ textAlign: 'center' }} width="100%">
                  <Box>
                    <h6>
                      {stockIsEmpty && emptyStockText}
                      {stockIsHiddenByFilters && t('filters.emptyFilter')}
                    </h6>
                  </Box>
                </Box>
              )}

              <div style={{ height: '100%', width: '100%' }}>
                <ResponsiveGrid
                  items={filteredStock}
                  selectedSize={selectedSize}
                  openPriceSuggestDrawer={openPriceSuggestDrawer}
                  openReservedQuantityDrawer={openReservedQuantityDrawer}
                  selectArticle={selectArticle}
                  onDeleteArticle={onDeleteArticle}
                  revertArticleEdit={revertArticleEdit}
                  showLocations={showLocations}
                  currency={user.currency}
                  parentPage={parentPage}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {parentPage === LISTING_AND_APPRAISAL && (
        <>
          {user.featureFlags?.taxation &&
            openCreateLotDialog &&
            (() => {
              const tabStock = tabMenuTabId ? allListingStocks?.[tabMenuTabId] : undefined
              const allItems = [...(tabStock?.stock ?? []), ...(tabStock?.sealedStock ?? [])]
              const prefillQuantity = allItems.reduce((sum, a) => sum + (a.quantity ?? 1), 0)
              const prefillPrice = allItems.reduce(
                (sum, a) => sum + (a.buyPrice ?? 0) * (a.quantity ?? 1),
                0
              )
              return (
                <ClosableDialog
                  fullWidth
                  maxWidth="lg"
                  open={openCreateLotDialog}
                  onClose={() => setOpenCreateLotDialog(false)}
                >
                  <PurchaseEventCreation
                    tabName={currentTab?.title}
                    initialQuantity={prefillQuantity || undefined}
                    initialPrice={prefillPrice ? roundTo2Decimals(prefillPrice) : undefined}
                    onLotCreated={(createdLot) => {
                      setOpenCreateLotDialog(false)
                      if (tabMenuTabId && onAssociateListingTabToLot) {
                        onAssociateListingTabToLot(tabMenuTabId, createdLot)
                      }
                      setTabMenuAnchorEl(null)
                    }}
                  />
                </ClosableDialog>
              )
            })()}

          {confirmCloseTabDialog && (
            <Dialog
              open={confirmCloseTabDialog}
              onClose={() => {
                setConfirmCloseTabDialog(false)
                setTabIdToClose(null)
              }}
            >
              <div className="p-4">
                <DialogTitle disableTypography>
                  <h3>{t('common.confirm')}</h3>
                </DialogTitle>
                <DialogContent>
                  <div className="d-flex flex-column">
                    <span className="mb-3">{t('inventory.tabs.confirmDeleteTab')}</span>
                    {allListingStocks![tabIdToClose!].stock.length > 0 && (
                      <span
                        className={`mb-1${
                          activeStockType !== 'singles' ? ' d-flex align-items-center' : ''
                        }`}
                      >
                        {t('inventory.tabs.singlesListed')}:{' '}
                        {allListingStocks![tabIdToClose!].stock.length}{' '}
                        {allListingStocks![tabIdToClose!].stock.length === 1
                          ? t('common.item')
                          : t('common.items')}
                        {activeStockType !== 'singles' && (
                          <i
                            className="fa-solid fa-triangle-exclamation ms-2"
                            style={{ color: 'var(--warning-color)' }}
                          />
                        )}
                      </span>
                    )}
                    {allListingStocks![tabIdToClose!].sealedStock.length > 0 && (
                      <span
                        className={`mb-1${
                          activeStockType !== 'sealed' ? ' d-flex align-items-center' : ''
                        }`}
                      >
                        {t('inventory.tabs.sealedListed')}:{' '}
                        {allListingStocks![tabIdToClose!].sealedStock.length}{' '}
                        {allListingStocks![tabIdToClose!].sealedStock.length === 1
                          ? t('common.item')
                          : t('common.items')}
                        {activeStockType !== 'sealed' && (
                          <i
                            className="fa-solid fa-triangle-exclamation ms-2"
                            style={{ color: 'var(--warning-color)' }}
                          />
                        )}
                      </span>
                    )}
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setConfirmCloseTabDialog(false)
                      setTabIdToClose(null)
                    }}
                    variant="outlined"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={() => {
                      if (tabIdToClose != null && onCloseListingTab) {
                        onCloseListingTab(tabIdToClose)
                      }
                      setConfirmCloseTabDialog(false)
                      setTabIdToClose(null)
                    }}
                    variant="contained"
                    color="secondary"
                  >
                    {t('common.confirmDeleteButton')}
                  </Button>
                </DialogActions>
              </div>
            </Dialog>
          )}

          <Menu
            anchorEl={tabMenuAnchorEl}
            open={Boolean(tabMenuAnchorEl)}
            onClose={() => {
              setTabMenuAnchorEl(null)
            }}
            PaperProps={
              {
                'data-testid': 'edit-tab-options',
              } as React.HTMLAttributes<HTMLDivElement>
            }
          >
            <MenuItem
              disabled={isAssociated}
              data-testid="rename-tab"
              onClick={() => {
                if (!currentTab || !listingTabsState) {
                  setTabMenuAnchorEl(null)
                  return
                }

                setEditingTitle(currentTab.title ?? '')
                setEditingTabId(currentTab.id)

                setTimeout(() => {
                  renameInputRef.current?.focus()
                  renameInputRef.current?.select()
                }, 0)

                setTabMenuAnchorEl(null)
              }}
            >
              {t('inventory.tabs.renameTab')}
            </MenuItem>

            {listingTabsState?.tabs
              .filter((tab) => tab.id !== listingTabsState.activeTabId)
              .map((tab) => {
                return (
                  <MenuItem
                    onClick={() => {
                      moveAllStockToTab && moveAllStockToTab(tab.id)
                      setTabMenuAnchorEl(null)
                    }}
                    key={tab.id}
                    data-testid={`move-to-${tab.id}`}
                  >
                    {t('inventory.tabs.moveAllItems')} {tab.title}
                  </MenuItem>
                )
              })}

            {/* UNASSOCIATED TAB OPTIONS (incl. Tab Zero) */}
            {user.featureFlags?.taxation &&
              !isAssociated && [
                <MenuItem
                  key={'turn-new-purchase-event'}
                  data-testid="turn-new-purchase-event"
                  disabled={!canAddTab && isTabZero} // il requisito blocca l'azione quando si supererebbe il limite
                  onClick={() => {
                    if (!currentTab) return
                    setOpenCreateLotDialog(true)
                    setTabMenuAnchorEl(null)
                  }}
                >
                  {t('inventory.tabs.turnIntoPe')}
                </MenuItem>,
                <MenuItem
                  data-testid="link-to-existing-pe"
                  key={'link-to-existing-pe'}
                  disabled={!canAddTab && isTabZero}
                  onClick={async () => {
                    if (!tabMenuAnchorEl) return
                    setTabMenuAnchorEl(null)
                    setLotMenuAnchorEl(tabMenuAnchorEl)
                  }}
                >
                  {t('inventory.tabs.linkToExistingPe')}
                </MenuItem>,
              ]}

            {/* ASSOCIATED TAB OPTIONS */}
            {user.featureFlags?.taxation &&
              isAssociated && [
                <MenuItem
                  data-testid="open-pe-details"
                  key={'open-pe-details'}
                  onClick={() => {
                    const lot = purchaseEvents.find((event) => event.id === currentTab?.lotId)
                    setCurrentPurchaseEvent(lot)
                    setOpenPurchaseEventDetail(true)
                    setTabMenuAnchorEl(null)
                  }}
                >
                  {t('inventory.tabs.openPeDetails', { lotNumber: currentTab?.title })}
                </MenuItem>,

                <MenuItem
                  data-testid="unlink-from-pe"
                  key={'unlink-from-pe'}
                  onClick={() => {
                    if (currentTab && unlinkListingTabFromLot) {
                      unlinkListingTabFromLot(currentTab.id)
                    }
                    setTabMenuAnchorEl(null)
                  }}
                >
                  {t('inventory.tabs.unlinkFromPe', { lotNumber: currentTab?.title })}
                </MenuItem>,
              ]}
          </Menu>

          {/* SUBMENU LOTTI */}
          <Menu
            anchorEl={lotMenuAnchorEl}
            open={Boolean(lotMenuAnchorEl)}
            onClose={() => {
              setLotMenuAnchorEl(null)
            }}
          >
            {loadingLots && <MenuItem disabled>{t('taxation.loadingPe')}</MenuItem>}

            {!loadingLots && purchaseEvents.length === 0 && (
              <MenuItem disabled>{t('taxation.noPeAvailables')}</MenuItem>
            )}

            {!loadingLots &&
              purchaseEvents
                .filter((item) => !item.seller?.name?.includes('Listing'))
                .map((lot) => (
                  <MenuItem
                    data-testid={'link-to-' + lot.lotNumber}
                    key={lot.id}
                    onClick={() => {
                      if (tabMenuTabId && onAssociateListingTabToLot) {
                        onAssociateListingTabToLot(tabMenuTabId, lot)
                      }
                      setLotMenuAnchorEl(null)
                    }}
                  >
                    {lot.lotNumber} - {lot.seller?.name} - {lot.price} €
                  </MenuItem>
                ))}
          </Menu>

          {user.featureFlags?.taxation && (
            <Drawer open={openPurchaseEventDetail} anchor="right">
              {currentPurchaseEvent ? (
                <PurchaseEventDetails
                  lot={currentPurchaseEvent}
                  handleClose={() => {
                    setOpenPurchaseEventDetail(false)
                    setCurrentPurchaseEvent(undefined)
                  }}
                  purchaseEvents={[]}
                  setHasPurchases={() => false}
                  setPurchaseEvents={() => []}
                  viewOnly
                />
              ) : (
                <span>{t('taxation.notFound')}</span>
              )}
            </Drawer>
          )}
        </>
      )}
    </>
  )
})
